import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

import { Dialog } from "../../components/Dialog";
import { nl } from "../../i18n/nl";
import { DESKTOP_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { SearchPanel } from "./SearchPanel";

type OpenSearch = () => void;

const SearchContext = createContext<OpenSearch | null>(null);

/** "Check een bedrijf": a ⌘K/Ctrl+K dialog on desktop, the `/zoeken` screen on mobile. */
export function SearchProvider({ children }: { children: ReactNode }) {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const openSearch = useCallback(() => {
    if (desktop) setOpen(true);
    else void navigate("/zoeken");
  }, [desktop, navigate]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey) && !event.altKey) {
        event.preventDefault();
        openSearch();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openSearch]);

  return (
    <SearchContext.Provider value={openSearch}>
      {children}
      <Dialog open={open} onOpenChange={setOpen} title={nl.search.title} size="wide">
        {open && (
          <SearchPanel
            autoFocusInput
            onNavigate={() => {
              setOpen(false);
            }}
          />
        )}
      </Dialog>
    </SearchContext.Provider>
  );
}

export function useSearch(): OpenSearch {
  const open = useContext(SearchContext);
  if (!open) throw new Error("useSearch must be used inside <SearchProvider>");
  return open;
}
